/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import {AutoFocusPlugin} from '@lexical/react/LexicalAutoFocusPlugin';
import {ClearEditorPlugin} from '@lexical/react/LexicalClearEditorPlugin';
import {HashtagPlugin} from '@lexical/react/LexicalHashtagPlugin';
import {LinkPlugin} from '@lexical/react/LexicalLinkPlugin';
import {ListPlugin} from '@lexical/react/LexicalListPlugin';
import {RichTextPlugin} from '@lexical/react/LexicalRichTextPlugin';
import ActionsPlugin from './plugins/ActionsPlugin';
import AutoEmbedPlugin from './plugins/AutoEmbedPlugin';
import AutoLinkPlugin from './plugins/AutoLinkPlugin';
import ClickableLinkPlugin from './plugins/ClickableLinkPlugin';
import ComponentPickerPlugin from './plugins/ComponentPickerPlugin';
import KeywordsPlugin from './plugins/KeywordsPlugin';
import {MaxLengthPlugin} from './plugins/MaxLengthPlugin';
import MentionsPlugin from './plugins/MentionsPlugin';
import TabFocusPlugin from './plugins/TabFocusPlugin'; 
import ToolbarPlugin from './plugins/ToolbarPlugin';
import ContentEditable from './ui/ContentEditable';
import Placeholder from './ui/Placeholder';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {$generateHtmlFromNodes, $generateNodesFromDOM} from '@lexical/html';
import {$createParagraphNode, $getRoot, $isElementNode} from 'lexical';
import {OnChangePlugin} from '@lexical/react/LexicalOnChangePlugin';
import { useEffect, useRef } from 'react';

const defaultContent = {
  root: {
    children: [
      {
        children:[],
        direction:null,
        format:"",
        indent:0,
        type:"paragraph",
        version:1
      }
    ],
    direction:null,
    format:"",
    indent:0,
    type:"root",
    version:1
  },
};

export default function Editor(props: any): JSX.Element {

  const {
    placeholder = "Bibliografía"
  } = props;

  const [editor] = useLexicalComposerContext();
  const humanEdited = useRef(false);
  
  useEffect(() => { editor.setEditable(!props.disabled); }, [editor, props.disabled]);

  // Prioridad: JSON Lexical persistido. Si una fila histórica tiene HTML
  // visible pero no JSON, reconstruir el estado desde ese HTML para que el
  // administrador nunca muestre vacío un texto que sí ve el usuario.
  useEffect (() => {
    humanEdited.current = false;
    if (props.content && !props.preferHtml) {
      const editorState = editor.parseEditorState(
        JSON.stringify(props.content)
      );
      editor.setEditorState(editorState);
      return;
    }

    const fallbackHtml = String(props.htmlContent || '').trim();
    if (fallbackHtml && typeof DOMParser !== 'undefined') {
      editor.update(() => {
        const dom = new DOMParser().parseFromString(
          fallbackHtml,
          'text/html'
        );
        const nodes = $generateNodesFromDOM(editor, dom);
        const root = $getRoot();
        root.clear();

        if (nodes.length > 0) {
          nodes.forEach(node => {
            if ($isElementNode(node)) root.append(node);
            else root.append($createParagraphNode().append(node));
          });
        } else {
          root.append($createParagraphNode());
        }
      });
      return;
    }

    const editorState = editor.parseEditorState(
      JSON.stringify(defaultContent)
    );
    editor.setEditorState(editorState);
  }, [editor, props.content, props.htmlContent, props.preferHtml]);

  //console.log("json", props.content);
  
  //console.log("jsonContent3", jsonContent);
  
  
  
  function onChange(editorState: { read: (arg0: () => void) => void; }) {
    
    if (props.disabled || (props.emitHumanChangesOnly && !humanEdited.current)) return;
    editorState.read(() => {
      // Read the contents of the EditorState here.
      //const jsonContent = $getRoot();
      //const selection = $getSelection();
      const htmlContent = $generateHtmlFromNodes(editor);
      
      const editorState = editor.getEditorState();
      const jsonContent = editorState.toJSON();

      props.handleEditorChange(htmlContent, jsonContent);
    });
  }


  
  return (
    <div
      onBeforeInputCapture={() => { humanEdited.current = true; }}
      onInputCapture={() => { humanEdited.current = true; }}
      onPasteCapture={() => { humanEdited.current = true; }}
      onCutCapture={() => { humanEdited.current = true; }}
      onDropCapture={() => { humanEdited.current = true; }}
      onKeyDownCapture={(event) => {
        if (event.key.length === 1 || ['Backspace', 'Delete', 'Enter'].includes(event.key)) humanEdited.current = true;
      }}
      onClickCapture={(event) => {
        if ((event.target as HTMLElement).closest('button')) humanEdited.current = true;
      }}
    >
      <ToolbarPlugin />
      <div
        className='editor-container tree-view'>
        <AutoFocusPlugin />
        <ClearEditorPlugin />
        <ComponentPickerPlugin />
        <AutoEmbedPlugin />
        <MentionsPlugin />
        <HashtagPlugin />
        <KeywordsPlugin />
        <AutoLinkPlugin />

        <RichTextPlugin
          contentEditable={
            <div className="editor-scroller">
              <div className="editor" >
                <ContentEditable />
              </div> 
            </div>
          }
          placeholder={<Placeholder>{placeholder}</Placeholder>}
        />
        <ListPlugin /> 
        <LinkPlugin />
        <OnChangePlugin onChange={onChange} ignoreSelectionChange={props.emitHumanChangesOnly} />
        <ClickableLinkPlugin />
        <TabFocusPlugin />
        <ActionsPlugin isRichText={true} />
      </div>
    </div>
  );
}

